// only allow certain browsers
import './browserCheck.js';
// Minimal shim for document.timeline.play(), the one non-native Web Animations feature
// @polymer/neon-animation relies on (paper-dialog / iron-dropdown transitions). Replaces the
// removed web-animations-js polyfill; all other Web Animations usage in the app is native.
// To remove this shim entirely: eliminate all reliance on @polymer/neon-animation's runner
// (NeonAnimationRunnerBehavior, which calls document.timeline.play) — migrate paper-dialog,
// iron-dropdown, and the custom katapult-*-animation / fade / scale animations to native
// element.animate() or CSS transitions. Once nothing calls document.timeline.play(), this
// shim can be deleted.
(function () {
  var timeline = document.timeline;
  if (timeline && typeof timeline.play !== 'function' && typeof Animation === 'function') {
    timeline.play = function (effect) {
      var animation = new Animation(effect, timeline);
      animation.play();
      return animation;
    };
  }
})();
import { PolymerElement } from '@polymer/polymer';

// keep track if we are using mobile
self.isMobile = location.pathname.includes('mobile');
// keep track if the origin is a development environment
import {
  checkIsDevelopmentOrigin,
  checkIsInternalOrigin,
  checkIsLegacyOrigin,
  checkIsChromeExtensionOrigin
} from '../modules/CheckOrigin.js';

// Load firebase and make global.
import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import 'firebase/compat/auth';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import 'firebase/compat/firestore';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getPerformance } from 'firebase/performance';
self.firebase = firebase;

import './config.js';
import { ParseCSSColor } from '../modules/_deprecated/ParseCSSColor.js';
import '../js/firebaseWorker/firebaseWorker.js';
import AppName from './appName.js';
import { initPageSleepManager } from '../modules/page-sleep-manager/page-sleep-manager-service.js';
import { initSentry } from './sentry.js';
import { initPostHog } from './posthog.js';
import { initIntercom } from './intercom.js';
import 'polymerfire/polymerfire.js';
import '../elements/katapult-firebase-worker/katapult-firebase-worker.js';
import { init as InitFeatureFlags } from '../modules/FeatureFlags.js';
import { getGeoEventsParser } from '../modules/update-parsers/GeoEventsParser.js';
import { getLastUpdatedParser } from '../modules/update-parsers/LastUpdatedParser.js';
import { getAttributeEventsParser } from '../modules/update-parsers/AttributeEventsParser.js';
import { getNodeLastUpdatedParser } from '../modules/update-parsers/NodeLastUpdatedParser.js';
import { getEntityDeletedParser } from '../modules/update-parsers/EntityDeletedParser.js';
import { KLogic } from '../modules/KLogic.js';

/** Initialize firebase */
let appName = '';
let shouldUpdateSessionAppName = false;

// Check if the origin is a development or internal environment
const isDevelopmentOrigin = checkIsDevelopmentOrigin();
const isChromeExtensionOrigin = checkIsChromeExtensionOrigin();
const isInternalOrigin = checkIsInternalOrigin();
const isLegacyOrigin = checkIsLegacyOrigin();

// If you are a developer or you are on beta or this is a chrome extension
if (isDevelopmentOrigin || isInternalOrigin || isChromeExtensionOrigin) {
  if (isDevelopmentOrigin && typeof globalThis.lockedAppName !== 'undefined' && globalThis.lockedAppName) {
    shouldUpdateSessionAppName = true;
    appName = globalThis.lockedAppName;
  } else {
    // If the app name doesn't exist in the session storage, then we should update it.
    shouldUpdateSessionAppName = !AppName.getSessionStorage();
    // Get the app name (either from session or local storage).
    appName = AppName.get();
  }
}

// Initialize Sentry on production (except for dev and legacy domains to avoid false regressions)
if (!isDevelopmentOrigin && !isLegacyOrigin) initSentry(appName || globalThis.config.appName);
// Initialize PostHog Analytics (except for dev domains to avoid false data)
if (!isDevelopmentOrigin) initPostHog();
if (!isDevelopmentOrigin) initIntercom(appName || globalThis.config.appName);

// set the config appName if necessary
if (appName) self.config.appName = appName;
if (firebase.apps.length) throw new Error('Failed to initialize firebase app: app already exists!');
let firebaseConfig = self.config.firebaseConfigs[self.config.appName];
if (!firebaseConfig)
  throw new Error('Failed to initialize firebase app: no firebase config found for app name ' + (self.config.appName || null) + '!');
else {
  const app = firebase.initializeApp(firebaseConfig);
  // Performance monitoring is only available for web apps with an appId. Some of our old projects don't have this.
  // TODO (2025-05-14): Give all projects an `appId` and remove this check
  if (firebaseConfig.appId) self.firebasePerformance = getPerformance(app);

  if (isDevelopmentOrigin) await connectToEmulatorsIfAvailable();

  // if we got the appName from indexedDB and we are using a database switching capable domain then set the appName in session storage
  // also if this is a locked page then update the appName in session storage to prevent false data
  if (shouldUpdateSessionAppName) AppName.setSessionStorage(self.config.appName);
  // Initialize FirebaseWorker
  FirebaseWorker._init();
  self.dispatchEvent(new CustomEvent('firebase-app-initialized'));

  // Set up feature flagging
  InitFeatureFlags(FirebaseWorker.database());

  // Set up the page sleep manager to manage firebase's online/offline state based on page visibility
  initPageSleepManager(firebase.database());

  monitorTokenRefresh();

  // Enable offline persistence for Firestore if we are on a mobile device
  if (self.isMobile) {
    firebase
      .firestore()
      .enablePersistence({ synchronizeTabs: true })
      .then(() => {
        console.info('Enabled offline persistence for Firestore');
      })
      .catch((err) => {
        console.warn('Failed to enable offline persistence:', err);
      });
  }
}

/**
 * Sets up a monitor to watch if the user's token has a refresh time that
 * differs from what we store locally and updates the token if so.
 */
function monitorTokenRefresh() {
  // We want to refresh the token if the updated time does not match our locally tracked time
  let disconnectUserTokenListener = null;
  const localTokenRefreshTimeStoragePath = `${globalThis.config.appName}_token_refresh_time`;
  globalThis.firebase.auth().onAuthStateChanged((user) => {
    // Remove previous listener if it exists
    disconnectUserTokenListener?.();
    // On user login add new listener
    if (user) {
      // Get a reference to the user's token refresh time
      const userTokenRefreshRef = globalThis.firebase.database().ref(`token_status/${user.uid}/refresh_time`);
      // Setup a callback to force refresh to pick up the latest custom claims changes
      const tokenRefreshCallback = (s) => {
        // If the user has a null refresh_time from the database and we store that in local
        // storage, it will be stored as the string "null". But if we get a value back from
        // local storage that is actually null, that means the value was never set. So if
        // the local value actually is null, we should update the token. Otherwise, we should
        // update the token only when the local and database values don't match (handling the case
        // of null from the database and the string "null" from local storage)

        // Fall back to the string "null" if the value is null to help the local storage comparison
        const refreshTime = s.val() || 'null';
        const localTokenRefreshTime = localStorage?.getItem(localTokenRefreshTimeStoragePath);
        // Check if the local storage value is actually null, meaning it was never set
        const localTimeIsNull = localTokenRefreshTime === null;
        if (localTimeIsNull || localTokenRefreshTime != refreshTime) {
          user.getIdToken(true);
          localStorage?.setItem(localTokenRefreshTimeStoragePath, refreshTime);
        }
      };
      // Setup listener to disconnect the callback.
      disconnectUserTokenListener = () => {
        userTokenRefreshRef.off('value', tokenRefreshCallback);
        disconnectUserTokenListener = null;
      };
      // Subscribe new listener
      userTokenRefreshRef.on('value', tokenRefreshCallback);
    }
  });
}

async function connectToEmulatorsIfAvailable() {
  // Fetch emulator info from the proxy set up in web-dev-server.config.mjs (this avoids needless 404 errors in the console)
  const response = await fetch(`${window.location.origin}/emulators`).catch(() => null);
  const emulators = (await response?.json()) || {};

  // If emulators are not running, there's nothing else to do
  if (!emulators.running) return;

  // Set up whatever emulators are available
  if (emulators.functions) {
    firebase.functions().useEmulator('localhost', emulators.functions.port);
    console.log('[Emulators] Functions emulator connected');
  }
  if (emulators.auth) {
    firebase.auth().useEmulator(`http://localhost:${emulators.auth.port}`);
    console.log('[Emulators] Auth emulator connected');
  }
  if (emulators.firestore) {
    firebase.firestore().useEmulator('localhost', emulators.firestore.port);
    connectFirestoreEmulator(getFirestore(), 'localhost', emulators.firestore.port);
    console.log('[Emulators] Firestore emulator connected');
  }
  if (emulators.database) {
    firebase.database().useEmulator('localhost', emulators.database.port);
    console.log('[Emulators] Database emulator connected');
  }

  Object.assign(globalThis.config, { emulators });
}

export class AppConfiguration {
  static get config() {
    return self.config;
  }
  constructor(polymerElement, options) {
    options = options || {};
    // Store a reference to the polymer element, the name of the property of the element which is to store the configuration data, and a local copy of configuration data.
    this.polymerElement = polymerElement || null;
    this.elementName = this.polymerElement ? `<${this.polymerElement.localName}>` : '(No Element Given)';
    this.configKey = options.propertyName || 'config';

    let localStorageFirebaseData = JSON.parse(self.localStorage.getItem(`${self.config.appName}_white_label`) || null);
    if (localStorageFirebaseData && !localStorageFirebaseData.error) {
      self.config.firebaseData = localStorageFirebaseData;
      // Write local data (in config.js) and default palette (or stored palette) to element.
      this.writeConfigToElement();
      this.applyPalette();
    }

    // Fetch app data stored in firebase.
    FirebaseWorker.ref('photoheight/white_label')
      .once('value')
      .then((snapshot) => {
        // Once loaded, update config, write the new data to the element, and update the styling of the element using any styling found in firebase.
        self.config.firebaseData = snapshot.val();
        this.writeConfigToElement();
        this.applyPalette();
        // Store firebase data in local storage for immediate use next time.
        self.localStorage.setItem(`${self.config.appName}_white_label`, JSON.stringify(self.config.firebaseData));
        // Run firebase data loaded callback.
        if (options.dataLoaded && typeof options.dataLoaded === 'function') options.dataLoaded(self.config);
      })
      .catch((err) => {
        console.warn('appConfiguration firebase data failed to load in ' + this.elementName + '\n', err);
      });

    // Attach update parsers (will auto-detach existing parsers if this constructor is somehow called more than once)
    globalThis.FirebaseWorker.attachUpdateParser(
      'attribute-event-parser',
      getAttributeEventsParser({ SOURCE: self.isMobile ? 'mobile' : 'desktop' })
    );
    globalThis.FirebaseWorker.attachUpdateParser('last-updated-parser', getLastUpdatedParser());
    globalThis.FirebaseWorker.attachUpdateParser('geo-events-parser', getGeoEventsParser());
    globalThis.FirebaseWorker.attachUpdateParser('node-last-updated-parser', getNodeLastUpdatedParser());
    globalThis.FirebaseWorker.attachUpdateParser('entity-deleted-parser', getEntityDeletedParser());

    // Listen for user permissions to be set
    if (self.katapultAuth) {
      katapultAuth.addEventListener('user-permissions-set', () => {
        // Setup action tracking.
        if (this.polymerElement.isKatapultPageElement) {
          const userGroup = katapultAuth.userGroup;

          //Company Level Listener: re-register when action tracking config is saved
          // Detach previous listener if userGroup changed or re-fired
          if (this.actionTrackingConfigRef && this.actionTrackingConfigCallback) {
            this.actionTrackingConfigRef.off('value', this.actionTrackingConfigCallback);
          }

          this.actionTrackingConfigRef = FirebaseWorker.ref(`photoheight/company_space/${userGroup}/project_tracking/action_tracking`);

          this.actionTrackingConfigCallback = (s) => {
            this.companyActionTrackingModels = s.val();
            if (this.companyActionTrackingModels) this.companyActionTrackingModels.userGroup = userGroup;
            if (this.activeJobId != null) {
              const model = this.computeActionTrackingModel(
                this.companyActionTrackingModels ?? {},
                this.activeJobMetadata ?? {},
                this.activeJobId
              );
              FirebaseWorker.registerActionTrackingForJob(model, userGroup, this.activeJobId);
            }
          };
          this.actionTrackingConfigRef.on('value', this.actionTrackingConfigCallback);

          //Job Level Listener: re-register when the active job changes (or when the job's metadata changes)
          // For each variant of job-id, create a property observer for action tracking
          ['jobId', 'jobid', 'job_id'].forEach((prop) => {
            this.polymerElement._createPropertyObserver(prop, (val) => this.registerActionTrackingForActiveJob(val));
            let initVal = this.polymerElement.get(prop);
            if (initVal) this.registerActionTrackingForActiveJob(initVal);
          });
        }

        // Register cached jobs so offline pending changes can sync
        if (self.isMobile) this.registerActionTrackingForCachedJobs();
      });
    }
  }

  applyPalette() {
    // Loop through all keys in config.firebaseData.palette, convert them to css variable names, and apply the values to them.
    if (self.config.firebaseData.palette) {
      let palette = self.config.firebaseData.palette;
      let lightColor = '#FFFFFF';
      let darkColor = '#212121';
      // Ensure default text colors exist.
      if (!palette.primaryTextColor) palette.primaryTextColor = darkColor;
      let rgb = ParseCSSColor(darkColor);
      let faded = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${rgb[3] - 0.2})`;
      if (!palette.primaryTextColorFaded) palette.primaryTextColorFaded = faded;
      // Calc text for color versions.
      ['primaryColor', 'secondaryColor'].forEach((x) => {
        let color = this.colorBrightness(ParseCSSColor(palette[x])) > 0.6 ? darkColor : lightColor;
        let rgb = ParseCSSColor(color);
        let faded = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${rgb[3] - 0.2})`;
        if (!palette[x + 'TextColor']) palette[x + 'TextColor'] = color;
        if (!palette[x + 'TextColorFaded']) palette[x + 'TextColorFaded'] = faded;
      });

      // Setup shoelace colors.
      palette.slColorPrimary = palette.secondaryColor;
      palette.slColorPrimaryText = palette.secondaryColorTextColor;
      palette.slColorSecondary = palette.primaryColor;
      palette.slColorSecondaryText = palette.primaryColorTextColor;

      for (let key in palette) {
        let propertyName =
          '--' +
          key
            .replace(/\W+/g, '-')
            .replace(/([a-z\d])([A-Z])/g, '$1-$2')
            .toLowerCase();
        let val = palette[key];
        document.documentElement.style.setProperty(propertyName, val);
      }
      window.dispatchEvent(new CustomEvent('palette-applied'));
    }
  }

  colorBrightness(rgb) {
    // Takes in a color and determines how bright it is on a scale from 0-255
    return Math.sqrt(rgb[0] * rgb[0] * 0.241 + rgb[1] * rgb[1] * 0.691 + rgb[2] * rgb[2] * 0.068) / 255;
  }

  writeConfigToElement() {
    if (this.polymerElement && this.polymerElement instanceof PolymerElement) {
      // Set the configKey property on the element to be the configuration data.
      this.polymerElement.set(this.configKey, self.config);
      // Notify all paths on the element for config.firebaseData.*.
      for (let key in self.config.firebaseData) {
        this.polymerElement.notifyPath(this.configKey + '.firebaseData.' + key);
      }
    } else this.polymerElement[this.configKey] = self.config;
  }

  async registerActionTrackingForActiveJob(jobId) {
    // Clear the existing action tracking models except for mobile
    if (!self.isMobile) FirebaseWorker.registerActionTrackingForJob(null, null, jobId);

    if (jobId == null) {
      // Detach previous metadata listener (if any) so we don't keep tracking the last active job
      this.metadataRef?.off('value', this.metadataListener);
      this.metadataRef = null;
      this.metadataListener = null;
      this.activeJobId = null;
      this.activeJobMetadata = null;
      console.debug('Action tracking and attribute tracking disabled in', this.elementName);
      return;
    }

    // Return if jobId is an empty string
    if (!jobId) return;

    if (!katapultAuth.userGroup) return;

    this.activeJobId = jobId;

    const userGroup = katapultAuth.userGroup;
    if (this.companyActionTrackingModels?.userGroup != userGroup) {
      this.companyActionTrackingModels = await FirebaseWorker.ref(`photoheight/company_space/${userGroup}/project_tracking/action_tracking`)
        .once('value')
        .then((s) => s.val());
      if (this.companyActionTrackingModels) this.companyActionTrackingModels.userGroup = userGroup;
    }

    if (!this.companyActionTrackingModels) {
      // Notify firebase worker that there are no action tracking models.
      FirebaseWorker.registerActionTrackingForJob({}, katapultAuth.userGroup, jobId);
      return;
    }

    const jobRef = FirebaseWorker.ref(`photoheight/jobs/${jobId}`);

    // Detach tracking model listener
    if (this.metadataListener && this.metadataRef) this.metadataRef.off('value', this.metadataListener);
    // Get the job's tracking model
    this.metadataRef = jobRef.child(`metadata`);
    // Listen for metadata changes and re-register the active job's tracking model
    this.metadataListener = this.metadataRef.on('value', async (s) => {
      const jobMetadata = s.val() ?? {};
      this.activeJobMetadata = jobMetadata;
      const actionTrackingModel = this.computeActionTrackingModel(this.companyActionTrackingModels, jobMetadata, jobId);
      FirebaseWorker.registerActionTrackingForJob(actionTrackingModel, katapultAuth.userGroup, jobId);
    });
  }

  async registerActionTrackingForCachedJobs() {
    const userGroup = katapultAuth?.userGroup;
    if (!userGroup) return;

    // Get the action tracking models for the current user group
    const fetchedActionTrackingModelRef = FirebaseWorker.ref(`photoheight/company_space/${userGroup}/project_tracking/action_tracking`);
    const fetchedActionTrackingModel = (await fetchedActionTrackingModelRef.once('value')).val();

    // Get the list of cached jobs once and register action tracking for each
    const cachedJobs = await globalThis.FirebaseWorker.getOfflineJobs();
    for (const cachedJob of cachedJobs) {
      const cachedJobId = cachedJob.jobId;
      if (!cachedJobId) continue;

      // Get the metadata for the cached job to compute the tracking model
      const cachedJobMetadataRef = FirebaseWorker.ref(`photoheight/jobs/${cachedJobId}/metadata`);
      const cachedJobMetadata = (await cachedJobMetadataRef.once('value')).val() ?? {};

      // Compute the tracking model for this cached job and register it with the FirebaseWorker
      const actionTrackingModel = this.computeActionTrackingModel(fetchedActionTrackingModel, cachedJobMetadata, cachedJobId);
      FirebaseWorker.registerActionTrackingForJob(actionTrackingModel, userGroup, cachedJobId, { isNotActiveJob: true });
    }
  }

  computeActionTrackingModel(fetchedActionTrackingModel, jobMetadata, jobId) {
    // Collect actions from each company model whose job filter matches this job's metadata
    const trackingModels = [];
    for (const key in fetchedActionTrackingModel) {
      const model = fetchedActionTrackingModel[key];
      if (KLogic.compute(model.job_filter, { job_id: jobId, metadata: jobMetadata })) {
        trackingModels.push(model.actions);
      }
    }

    // Merge all matching action sets into a single flat tracking model
    return trackingModels.length ? Object.assign({}, ...trackingModels) : {};
  }
}
self.AppConfiguration = AppConfiguration;
